import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { uniqBy } from 'lodash';
import { Stack } from '@koob/margaret';
import { KoobInputSelect } from '@koob/ui';
import {
  toOptionalSupplementOptions,
  resolveSupplementOnBed,
} from '@koob/utils';

const RoomExtraContent = styled(Stack)`
  padding-left: 0;
  padding-right: ${({ theme }) => theme.spacing()};

  p {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  }
`;

export const Promotion = styled(Stack).attrs({
  alignY: 'center',
  gutterSize: 0.25,
  marginBottom: 0.5,
})`
  font-size: 0.75rem;
  padding: ${({ theme }) => theme.spacing(0.25)}
    ${({ theme }) => theme.spacing(0.75)};
  border-radius: 40px;
  color: #2e1065;
  background-color: ${({ bgColor }) => bgColor ?? '#e9d5ff'};

  ${(props) =>
    props.promo &&
    css`
      padding: ${({ theme }) => theme.spacing(0.2)};
      margin-top: -1px !important;
    `}
`;

export const BedTag = styled(Stack).attrs({
  gutterSize: 0.5,
  alignY: 'center',
})`
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.separator};
  padding: 3px 10px;
  ${(props) =>
    props.selected &&
    css`
      background: ${({ theme }) => theme.orangeLight2};
      color: ${({ theme }) => theme.orange};
    `}
  ${(props) =>
    props.red &&
    css`
      background-color: #ffe3e3;
      color: #ea5d5d !important;
    `}
    ${(props) =>
    props.grey &&
    css`
      color: rgba(0, 0, 0, 0.5);
      background-color: #eeeeee;
    `}
    ${(props) =>
    props.green &&
    css`
      background: rgba(115, 201, 146, 0.12);
      color: #73c992;
    `}

    border-radius: 5px;
`;

const Separator = styled.div`
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.separator};
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

export default function CheckoutHotelRoomSelectionRoomDetails({
  possibility,
  currentBed,
  currentBedIndex,
  changeSelectedBed,
  isSelected,
  isRoomSelected,
  deleteRoom,
  promotions,
  selectedRoomsFromPromotionSelect,
  selectedPromotionSupplements,
  setSelectedPromotionSupplements,
  selectedSupplements,
  setSelectedSupplements,
  currentSelectedRoom,
  setCurrentSelectedRoom,
  selectedRooms,
  translations,
  renderPromotionTag,
  renderRequiredPromotionTag,
  renderUpdateButton,
  supplementKey,
  updateRoomSupplements,
}) {
  const [values, setValues] = useState();
  const [selectedPromotion, setSelectedPromotion] = useState(0);
  const [clicked, setClicked] = useState(false);

  const selectedRoomsCount = selectedRooms.length ?? 0;
  let promotionIndex = 0;
  const promotionOptions =
    possibility.allPromotions
      ?.flatMap((promotionCombinations) => {
        return Object.entries(promotionCombinations).map(
          ([key, promotionCombination]) => {
            if (currentBed && promotionCombination.length) {
              const result = {
                label: promotionCombination.join(', '),
                value: key,
                index: promotionIndex,
                promotions: promotionCombination,
              };
              promotionIndex++;
              return result;
            } else {
              return null;
            }
          },
        );
      })
      .filter((item) => item !== null) ?? [];

  const uniquePromotionOptions = Array.from(
    new Map(promotionOptions.map((item) => [item.label, item])).values(),
  );

  const promotionOptionsFiltered = (uniquePromotionOptions || []).filter(
    (item) => !promotions.some((sup) => sup.value === item.label),
  );

  const koediaPromos = currentBed?.promotions?.filter((item) => item !== null);

  // The options are built from the supplements of the bed being shown, not
  // looked up by name in the selection-wide `promotions` list: that list is
  // deduplicated by name across every room, so a percentage supplement quoted
  // its FIRST room's amount — and its id — on every other room.
  const filteredOptionSupplement = uniqBy(
    toOptionalSupplementOptions(
      currentBed?.optionalSupplements ??
        possibility?.bedChoices?.[currentBedIndex]?.optionalSupplements,
    ),
    'displayName',
  );

  const handleClick = (e) => {
    if (clicked) {
      e.stopPropagation();
    } else {
      setClicked(true);
    }
  };

  // Replaces react-select's onMenuOpen: runs when the (enabled) select's
  // trigger is clicked, keeping the promotion in sync for non-selected rooms.
  const handleSelectTriggerClick = () => {
    if (isRoomSelected) {
      return;
    }
    setClicked(true);
    if (!isSelected && selectedRoomsFromPromotionSelect) {
      setSelectedPromotion(values?.index);
      selectedRoomsFromPromotionSelect(
        possibility.originalIndex,
        values?.label,
        currentBed.name,
      );
    }
    setClicked(false);
  };

  const renderPromotions = () => {
    const filteredPromotions = isRoomSelected
      ? currentBed?.promotions
      : promotionOptions[selectedPromotion]?.promotions;

    const supplements =
      currentBed?.totalPriceWithoutPromo !== currentBed?.totalPriceWithPromo
        ? currentBed?.mandatorySupplements
        : [];

    if (currentBed?.promotions?.length > 0 || supplements?.length > 0) {
      return (
        <RoomExtraContent size="full" direction="column" gutterSize={0.5}>
          <Separator />

          <Stack
            style={{
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            {filteredPromotions?.map((name, index) =>
              renderPromotionTag({ key: index, name }),
            )}

            {supplements?.map((name, index) =>
              renderRequiredPromotionTag({
                key: index,
                name,
                label: translations.requiredSupplement,
              }),
            )}
          </Stack>
        </RoomExtraContent>
      );
    }
  };

  useEffect(() => {
    if (isSelected && selectedPromotion !== 0) {
      setSelectedPromotion(values?.index ?? selectedPromotion);
      setClicked(false);
    }
    if (isSelected && selectedRoomsFromPromotionSelect) {
      selectedRoomsFromPromotionSelect(
        possibility.originalIndex,
        values?.label,
        currentBed?.name,
      );
    }
  }, [currentBedIndex, isSelected]);

  useEffect(() => {
    setSelectedPromotion(0);
  }, [isSelected]);

  useEffect(() => {
    if (!isSelected && selectedRoomsFromPromotionSelect) {
      setClicked(false);
      setSelectedPromotion(values?.index ?? 0);
      selectedRoomsFromPromotionSelect(
        possibility.originalIndex,
        values?.label,
        currentBed?.name,
      );
    }
  }, [selectedPromotion, values]);

  useEffect(() => {
    // `currentSelectedRoom` guards the spread below: with no room being chosen
    // it builds one out of nothing — a truthy `{ bed: { …: NaN } }` that then
    // counts as a pending room in the recap and in the extra-nights note.
    //
    // `selectedSupplements` is selection-wide, so every card reacts to it. Only
    // the card being chosen may fold it into `currentSelectedRoom`: any other
    // card re-prices that room off ITS OWN `currentBedIndex`, silently moving
    // the total back to a bed the user did not pick. A validated room is
    // excluded too — its supplements go through updateRoomSupplements.
    if (
      !selectedPromotion &&
      currentSelectedRoom &&
      isSelected &&
      !isRoomSelected
    ) {
      // A selection survives a bed change but carries the previous bed's ids
      // and amount, so it is re-read off the bed being booked before it is
      // priced in and stored on the room.
      const bedSupplements = (selectedSupplements ?? []).map((supplement) =>
        resolveSupplementOnBed(filteredOptionSupplement, supplement),
      );

      const promosPrice = bedSupplements.reduce(
        (total, { price }) => total + (price ?? 0),
        0,
      );

      const baseBed =
        currentSelectedRoom?.bedChoices?.[currentBedIndex] ??
        currentSelectedRoom?.bed ??
        currentBed;

      setCurrentSelectedRoom({
        ...currentSelectedRoom,
        bed: {
          ...baseBed,
          ...currentSelectedRoom?.bed,
          totalPriceWithPromo:
            (baseBed?.totalPriceWithPromo ?? 0) + promosPrice,
        },
        optionalPromos: bedSupplements,
      });
    }
    // `currentBedIndex` too: picking another bed configuration rebuilds
    // `currentSelectedRoom` from the raw possibility, which carries no
    // `optionalPromos`. Without re-running here the supplements stayed ticked in
    // the select but were gone from the room — off the price, off the payload,
    // and off the extra-nights note.
  }, [selectedSupplements, currentBedIndex]);

  // The supplements currently ticked on THIS room. It has to be read from the
  // same place onValueChange writes (`selectedPromotionSupplements`, keyed per
  // room), otherwise the select is a controlled input whose value never moves
  // and no option can be ticked. A validated room falls back to what it was
  // stored with, so reopening the page shows its real selection.
  const supplementStoreKey = supplementKey ?? possibility.originalIndex;
  const currentRoomSupplements = (
    selectedPromotionSupplements?.[supplementStoreKey] ??
    (isRoomSelected ? possibility.optionalPromos : []) ??
    []
  ).map((supplement) =>
    // Ids are per bed: a selection ticked on another bed configuration has to
    // be re-read off this one, or the option no longer appears ticked.
    resolveSupplementOnBed(filteredOptionSupplement, supplement),
  );

  const selectedPromo = promotionOptionsFiltered?.find((item) => {
    const selectedBedPromotions =
      selectedRooms?.[selectedRooms?.length - 1]?.bed?.promotions ?? [];
    if (selectedBedPromotions.length === 0) {
      return false;
    }
    // Compare against the full joined combo, not just the first promo,
    // so cumulative selections like "5%, 10%" still match the dropdown option.
    return item.label === selectedBedPromotions.join(', ');
  });

  const roomName = `${possibility?.name?.toLowerCase().split(' ').join('-')}`;

  return (
    <div className="flex-col space-y-3">
      {Array.isArray(possibility.allPromotions) === true &&
        possibility.allPromotions.length > 1 &&
        promotionOptionsFiltered.length > 1 && (
          <div>
            <div className="mb-1.5 text-lg font-medium">
              <i className="fal fa-tag mr-1.5" />
              {translations.choosePromotion}
            </div>

            <div
              onClick={(e) => {
                handleClick(e);
                handleSelectTriggerClick();
              }}
              className="w-[350px] max-w-full"
            >
              <KoobInputSelect
                placeholder={
                  translations.selectPromotionsPlaceholder ||
                  'Select a promotions'
                }
                data-testid={
                  !isRoomSelected
                    ? `select-promotion-${selectedRooms.length}`
                    : undefined
                }
                options={promotionOptionsFiltered.map((option) => ({
                  value: option.index,
                  label: option.label,
                }))}
                value={
                  (isRoomSelected
                    ? selectedPromo?.index
                    : promotionOptionsFiltered[selectedPromotion]?.index) ??
                  promotionOptionsFiltered[0]?.index ??
                  null
                }
                onValueChange={(selectedIndex) => {
                  const value = promotionOptionsFiltered.find(
                    (option) => option.index === selectedIndex,
                  );
                  if (!value) {
                    return;
                  }
                  setValues(value);
                  setSelectedPromotion(value.index);
                  if (selectedRoomsFromPromotionSelect) {
                    selectedRoomsFromPromotionSelect(
                      possibility.originalIndex,
                      value.label,
                      currentBed.name,
                    );
                  }
                }}
                disabled={isRoomSelected}
              />
            </div>
          </div>
        )}

      {Array.isArray(filteredOptionSupplement) === true &&
        filteredOptionSupplement?.length > 0 && (
          <div>
            <div className="mb-1.5 text-lg font-medium">
              <i className="fal fa-tag mr-1.5" />
              {translations.chooseOptionalPromotion}
            </div>

            <div
              onClick={(e) => {
                handleClick(e);
                handleSelectTriggerClick();
              }}
              className="w-[350px] max-w-full"
            >
              <KoobInputSelect
                key={possibility.originalIndex}
                placeholder={
                  translations.selectSupplementsPlaceholder ||
                  'Select supplements'
                }
                data-testid={
                  !isRoomSelected
                    ? `select-supplement-${selectedRooms.length}`
                    : undefined
                }
                value={currentRoomSupplements.map(
                  (supplement) => supplement?.id,
                )}
                options={filteredOptionSupplement.map((supplement) => ({
                  value: supplement?.id,
                  label: supplement?.displayName,
                }))}
                onValueChange={(ids) => {
                  const value = filteredOptionSupplement.filter((supplement) =>
                    (ids ?? []).includes(supplement?.id),
                  );
                  setSelectedPromotionSupplements((prev) => {
                    return {
                      ...prev,
                      [supplementStoreKey]: value,
                    };
                  });
                  if (isRoomSelected) {
                    // Already validated: `currentSelectedRoom` points elsewhere,
                    // so the change has to land on the stored room or it never
                    // reaches the booking payload.
                    updateRoomSupplements?.(value);
                  } else {
                    setSelectedSupplements(value);
                  }
                }}
                multiple
              />
            </div>
          </div>
        )}

      {renderPromotions()}

      {possibility?.supplier === 'Koedia' && koediaPromos?.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {koediaPromos?.map((name, index) =>
            renderPromotionTag({ key: index, name }),
          )}
        </div>
      )}

      {(possibility.bedChoices ?? []).length > 1 && (
        <div>
          <div className="mb-1.5 text-lg font-medium">
            <i className="fal fa-bed mr-1.5" />
            {translations.selectedComposition}
          </div>

          <div className="flex flex-wrap gap-3">
            {(possibility?.bedChoices ?? []).map((bedElement, index) => (
              <BedTag
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  // A validated room shows the bed it was stored with,
                  // whichever index the card started on, so the index alone
                  // cannot tell whether the clicked bed is the one shown.
                  if (
                    currentBedIndex !== index ||
                    (isRoomSelected && currentBed?.id !== bedElement?.id)
                  ) {
                    changeSelectedBed(index);
                  }
                }}
                selected={currentBed?.id === bedElement?.id}
              >
                <div
                  data-testid={
                    !isRoomSelected &&
                    `${bedElement.hasExtraBed ? 'extra_bed' : 'koob_bed'}_${roomName}_${selectedRoomsCount}`
                  }
                >
                  {bedElement.name}
                </div>
              </BedTag>
            ))}
          </div>
        </div>
      )}

      {isRoomSelected && renderUpdateButton && (
        <Stack size="full" alignX="end">
          {renderUpdateButton({ onClick: deleteRoom })}
        </Stack>
      )}
    </div>
  );
}
